import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LangProvider } from "@/context/LangContext";
import { api } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Workshops from "@/pages/Workshops";
import WorkshopRunner from "@/pages/WorkshopRunner";
import Simulator from "@/pages/Simulator";
import IdeasLab from "@/pages/IdeasLab";
import IdeaDetail from "@/pages/IdeaDetail";
import Matrix from "@/pages/Matrix";
import Profile from "@/pages/Profile";
import HRDashboard from "@/pages/HRDashboard";

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="app-loader">
      <div className="h-10 w-10 rounded-full border-4 border-indigo-200 border-t-indigo-600 animate-spin" />
    </div>
  );
}

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(location.hash.slice(1)).get("session_id");
    api.post("/auth/google/session", {}, { headers: { "X-Session-ID": sid } })
      .then(({ data }) => { setUser(data); navigate("/app", { replace: true }); })
      .catch(() => { setUser(false); navigate("/login", { replace: true }); });
  }, [location.hash, navigate, setUser]);
  return <Loader />;
}

function Protected({ children, manager }) {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (manager && !["manager", "admin"].includes(user.role)) return <Navigate to="/app" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/app" element={<Protected><AppLayout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="ateliers" element={<Workshops />} />
        <Route path="ateliers/:id" element={<WorkshopRunner />} />
        <Route path="simulateur" element={<Simulator />} />
        <Route path="idees" element={<IdeasLab />} />
        <Route path="idees/:id" element={<IdeaDetail />} />
        <Route path="matrice" element={<Matrix />} />
        <Route path="profil" element={<Profile />} />
        <Route path="rh" element={<Protected manager><HRDashboard /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LangProvider><AppRouter /></LangProvider>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
